import React from 'react';
import { hiResAvatar } from '../utils/avatar';

/* ═══════════════════════════════════════════════════════════════════
   BLISS STAMP — the perforated postage-stamp identity card originally
   rendered inside Essence › Stamp tab.  Extracted here so the same
   exact stamp can be re-used at reduced size on the Global User
   Profile (`/user/:email`) left column.

   Props:
     user         { nickname, name, email, profile_picture, member_number }
     totalUploads number (drives the rank line: INITIATE → CENTURION)
     maxWidth     px cap (default 340; pass ~120 for the mini variant)

   The layout is intentionally identical to EssenceVault → StampTabContent
   so the two surfaces stay visually consistent.
   ═══════════════════════════════════════════════════════════════════ */
export const BlissStamp = ({ user, totalUploads = 0, maxWidth = 340 }) => {
  const emailPrefix = (user?.email || '').split('@')[0].replace(/[._-]+/g, ' ').trim();
  const rawName = user?.nickname || user?.name || user?.first_name || emailPrefix || 'Bliss Member';
  const name = String(rawName).toUpperCase();
  const isFallback = !user?.nickname && !user?.name && !user?.first_name && !emailPrefix;
  const memberNumber = user?.member_number;
  const blissIdLabel = memberNumber ? String(memberNumber) : '—';
  const photo = hiResAvatar(user?.avatar_url || user?.photo_url || user?.profile_picture || null);
  const rank = totalUploads >= 100 ? 'CENTURION'
             : totalUploads >= 50 ? 'SOVEREIGN'
             : totalUploads >= 25 ? 'ELDER'
             : totalUploads >= 10 ? 'DISCIPLE'
             : 'INITIATE';

  // Perforation SVG math — scaled down proportionally for compact renders.
  const W = 200, H = 260;
  const isMini = maxWidth <= 180;
  const NOTCH_R = isMini ? 3 : 4;
  const SPACING = isMini ? 9 : 11;
  const buildEdgeDots = (length, along, cross) => {
    const count = Math.floor((length - SPACING) / SPACING);
    const start = (length - count * SPACING) / 2;
    return Array.from({ length: count + 1 }, (_, i) => ({
      cx: along === 'x' ? start + i * SPACING : cross,
      cy: along === 'y' ? start + i * SPACING : cross,
    }));
  };
  const dots = [
    ...buildEdgeDots(W, 'x', 0).map(d => ({ ...d, cy: 0 })),
    ...buildEdgeDots(W, 'x', H).map(d => ({ ...d, cy: H })),
    ...buildEdgeDots(H, 'y', 0).map(d => ({ ...d, cx: 0 })),
    ...buildEdgeDots(H, 'y', W).map(d => ({ ...d, cx: W })),
  ];

  // Typography auto-scales when miniaturised — bigger text on the full-size
  // Essence render, tighter text on the compact 120-160px sidebar version.
  const scale = maxWidth / 340;
  const fs = (px) => Math.max(6, Math.round(px * scale));
  const inset = Math.max(8, Math.round(22 * scale));
  const frameInset = Math.max(6, Math.round(16 * scale));

  return (
    <div className="relative w-full" style={{ maxWidth, aspectRatio: `${W} / ${H}` }} data-testid="bliss-stamp">
      {/* Cream stamp body */}
      <div className="absolute inset-0 rounded-[2px]"
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, #F6E9C6 0%, #E9D497 45%, #C9AA5F 100%)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35)',
        }}>
        <div className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            background:
              'repeating-linear-gradient(45deg, rgba(90,60,20,0.06) 0 1px, transparent 1px 4px),' +
              'repeating-linear-gradient(-45deg, rgba(90,60,20,0.06) 0 1px, transparent 1px 4px)',
          }}/>
      </div>

      {/* Perforation dots — match app background so they read as cutouts */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        {dots.map((d, i) => (
          <circle key={i} cx={d.cx} cy={d.cy} r={NOTCH_R} fill="#050308"/>
        ))}
      </svg>

      {/* Thin gold inner frame */}
      <div className="absolute pointer-events-none rounded-[1px]"
        style={{
          top: frameInset, left: frameInset, right: frameInset, bottom: frameInset,
          border: `${isMini ? 1 : 1.5}px solid #8B6E1A`,
          boxShadow: `inset 0 0 0 3px transparent, inset 0 0 0 ${isMini ? 2 : 4}px rgba(201,170,95,0.35)`,
        }}/>

      {/* Content stack */}
      <div className="absolute flex flex-col"
        style={{ top: inset, left: inset, right: inset, bottom: inset }}>
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <p className="font-['Cinzel'] font-bold tracking-[0.3em]"
            style={{ color: '#4A3208', fontSize: fs(11), textShadow: '0 1px 0 rgba(255,255,255,0.4)' }}>
            BLISS
          </p>
          <p className="font-['Cormorant_Garamond'] italic"
            style={{ color: '#8B6E1A', fontSize: fs(10) }}>
            Est. Society
          </p>
        </div>

        {/* Portrait */}
        <div className="relative flex items-center justify-center"
          style={{ flex: '55 1 0%', minHeight: 0 }}>
          {photo ? (
            <img src={photo} alt={name}
              className="w-full h-full object-cover"
              data-testid="bliss-stamp-photo"
              style={{
                filter: 'sepia(0.85) contrast(1.05) saturate(0.9) brightness(0.95)',
                mixBlendMode: 'multiply',
              }}/>
          ) : (
            <div className="rounded-full flex items-center justify-center"
              style={{
                width: '55%', aspectRatio: '1 / 1',
                background: 'linear-gradient(135deg, #C9AA5F, #8B6E1A)',
                border: '2px solid #6B4A15',
              }}>
              <span className="font-['Cinzel'] font-bold"
                style={{ color: '#4A3208', fontSize: fs(51) }}>
                {name.charAt(0)}
              </span>
            </div>
          )}
        </div>

        {/* Info block — at ~mini size the middle text lines physically can't
            fit at the required Cinzel tracking without overlapping the
            portrait, so we omit them and keep just the name + BLISS SOCIETY
            footer to preserve the exact stamp DNA at small sizes. */}
        <div className="mt-2 flex flex-col items-center text-center" style={{ flex: '0 0 auto' }}>
          <p className="font-['Cinzel'] font-bold tracking-[0.24em]"
            style={{ color: '#2A1B04', fontSize: fs(15), lineHeight: 1.15,
              textShadow: '0 1px 0 rgba(255,255,255,0.4)' }}
            data-testid="bliss-stamp-name">
            {name}
          </p>
          {!isMini && !isFallback && (
            <p className="font-['Outfit'] uppercase tracking-[0.32em] mt-0.5"
              style={{ color: '#6B4A15', fontSize: fs(9) }}>
              Bliss Member
            </p>
          )}
          {!isMini && (
            <p className="font-['Outfit'] uppercase tracking-[0.2em] mt-1.5"
              style={{ color: '#6B4A15', fontSize: fs(10) }}
              data-testid="bliss-stamp-id">
              Bliss ID · <span style={{ color: '#3A2606', fontWeight: 700 }}>{blissIdLabel}</span>
              <span style={{ color: '#8B6E1A', margin: '0 6px' }}>·</span>
              {rank}
            </p>
          )}
          <p className="font-['Cinzel'] tracking-[0.32em] mt-1.5 w-full"
            style={{ color: '#8B6E1A', fontSize: fs(9),
              borderTop: '1px solid rgba(139,110,26,0.4)', paddingTop: 4 }}>
            — BLISS SOCIETY —
          </p>
        </div>
      </div>
    </div>
  );
};

export default BlissStamp;
